import { zodResolver } from '@hookform/resolvers/zod';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { AccountSnapshotInputSchema } from 'shared';
import type { AccountSnapshotInput } from 'shared';

import { useUpsertSnapshot } from '../../features/snapshots/mutations';
import type { AccountDto } from '../../lib/api/endpoints';
import { formatCurrency } from '../../lib/format/currency';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';

interface NavInputDialogProps {
  account: AccountDto;
  baseCurrency: string;
  trigger: ReactNode;
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

export function NavInputDialog({ account, baseCurrency, trigger }: NavInputDialogProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const upsertSnapshot = useUpsertSnapshot();
  const form = useForm<AccountSnapshotInput>({
    resolver: zodResolver(AccountSnapshotInputSchema),
    defaultValues: {
      asOfDate: todayIsoDate(),
      nav: 1,
      cash: 0,
      marginUsed: 0,
      buyingPower: 0,
    },
  });
  const values = form.watch();

  const onReview = form.handleSubmit(() => {
    setStep('confirm');
  });

  function resetDialog(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) {
      setStep('form');
      form.reset({
        asOfDate: todayIsoDate(),
        nav: 1,
        cash: 0,
        marginUsed: 0,
        buyingPower: 0,
      });
    }
  }

  function submitConfirmed() {
    const input = AccountSnapshotInputSchema.parse(values);
    upsertSnapshot.mutate(
      { accountId: account.id, input },
      {
        onSuccess: () => resetDialog(false),
      },
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={resetDialog}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded border border-border bg-surface p-5 text-text shadow-xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-semibold">Update NAV</Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-text-muted">
                {account.name}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button aria-label="Close NAV dialog" size="sm" type="button" variant="ghost">
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </Dialog.Close>
          </div>

          {step === 'form' ? (
            <form className="mt-5 grid gap-4 sm:grid-cols-2" onSubmit={onReview}>
              <FormField
                label="As of date"
                type="date"
                error={form.formState.errors.asOfDate?.message}
                {...form.register('asOfDate')}
              />
              <FormField
                label="NAV"
                type="number"
                step="0.0001"
                error={form.formState.errors.nav?.message}
                {...form.register('nav', { valueAsNumber: true })}
              />
              <FormField
                label="Cash"
                type="number"
                step="0.0001"
                error={form.formState.errors.cash?.message}
                {...form.register('cash', { valueAsNumber: true })}
              />
              <FormField
                label="Margin used"
                type="number"
                step="0.0001"
                error={form.formState.errors.marginUsed?.message}
                {...form.register('marginUsed', { valueAsNumber: true })}
              />
              <FormField
                label="Buying power"
                type="number"
                step="0.0001"
                error={form.formState.errors.buyingPower?.message}
                {...form.register('buyingPower', { valueAsNumber: true })}
              />
              <div className="flex justify-end sm:col-span-2">
                <Button type="submit">Review update</Button>
              </div>
            </form>
          ) : (
            <div className="mt-5 space-y-5">
              <p className="text-sm text-text">
                Update NAV for {account.name} to{' '}
                <span className="font-semibold tabular-nums">
                  {formatCurrency(values.nav, baseCurrency)}
                </span>
                ? This replaces today&apos;s snapshot if one exists.
              </p>
              {upsertSnapshot.isError ? (
                <p className="text-sm text-danger">Could not update NAV.</p>
              ) : null}
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button type="button" variant="secondary" onClick={() => setStep('form')}>
                  Back
                </Button>
                <Button type="button" disabled={upsertSnapshot.isPending} onClick={submitConfirmed}>
                  {upsertSnapshot.isPending ? 'Saving...' : 'Confirm update'}
                </Button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
